import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Form from '@cloudscape-design/components/form';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import Container from '@cloudscape-design/components/container';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Textarea from '@cloudscape-design/components/textarea';
import Select from '@cloudscape-design/components/select';
import RadioGroup from '@cloudscape-design/components/radio-group';
import Alert from '@cloudscape-design/components/alert';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import Checkbox from '@cloudscape-design/components/checkbox';
import { programsApi, CreateProgramRequest } from '../api/programs';
import { useCreateProgram, useUpdateProgram, useDeleteProgram, useTransferProgramOwnership /*, usePublishProgram TODO: Restore when isPublished flow is fully tested */ } from '../hooks/queries/usePrograms';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { HelpPopover } from '../components/HelpPopover';
import { UserSearchSelect } from '../components/UserSearchSelect';
import { PersonChip } from '../components/PersonChip';
import { UserSearchResult } from '../api/users';
import { useBreadcrumbs } from '../contexts';
import { buildBreadcrumbs } from '../components/BreadcrumbBuilder';
import { badgePhotoUrl, phoneToolUrl } from '../lib/externalUrls';
import Link from '@cloudscape-design/components/link';
import Icon from '@cloudscape-design/components/icon';
import { useAuthStore } from '../stores/authStore';
import { ProtectedRoute } from '../components/ProtectedRoute';

interface ValidationErrors {
  programName?: string;
  description?: string;
  category?: string;
  wikiUrl?: string;
  orgLeader?: string;
}

// Program categories
const PROGRAM_CATEGORIES = [
  { label: 'Technical', value: 'Technical' },
  { label: 'Certification', value: 'Certification' },
  { label: 'Sales', value: 'Sales' },
  { label: 'Leadership', value: 'Leadership' },
];

function CreateProgram() {
  const navigate = useNavigate();
  const { programId } = useParams<{ programId: string }>();
  const { setBreadcrumbs } = useBreadcrumbs();
  const currentUser = useAuthStore((state) => state.user);
  const isEditMode = !!programId;
  
  // ── Edit form state ──────────────────────────────────────────────────────
  const [programName, setProgramName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<{ label: string; value: string } | null>(null);
  const [wikiUrl, setWikiUrl] = useState('');
  const [slackChannel, setSlackChannel] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'org-restricted'>('public');
  const [orgLeader, setOrgLeader] = useState<UserSearchResult | null>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [loadingProgram, setLoadingProgram] = useState(isEditMode);
  const [isProgramOwner, setIsProgramOwner] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cohortCount, setCohortCount] = useState<number>(0);
  // TODO: Restore when isPublished flow is fully tested
  // const [isPublished, setIsPublished] = useState(false);
  // const [showPublishModal, setShowPublishModal] = useState(false);
  // const [publishConfirmText, setPublishConfirmText] = useState('');

  // ── Danger Zone state (edit mode only) ──────────────────────────────────
  const [showTransferOwnerModal, setShowTransferOwnerModal] = useState(false);
  const [newOwner, setNewOwner] = useState<UserSearchResult | null>(null);
  const [transferOwnerError, setTransferOwnerError] = useState<string | null>(null);
  const [retainAsAdmin, setRetainAsAdmin] = useState(false);
  const [transferConfirmText, setTransferConfirmText] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const openTransferOwnerModal = () => {
    setNewOwner(null);
    setRetainAsAdmin(false);
    setTransferConfirmText('');
    setTransferOwnerError(null);
    setShowTransferOwnerModal(true);
  };

  const openDeleteModal = () => {
    setDeleteConfirmText('');
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const requiredFieldErrors: Record<string, string> = {
    programName: 'Program name is required.',
    description: 'Description is required.',
    category: 'Category is required.',
  };

  const clearError = (key: keyof ValidationErrors) => {
    if (errors[key]) {
      setErrors(prev => { const next = { ...prev }; delete next[key]; return next; });
    }
  };

  const setError = (key: keyof ValidationErrors, message: string) => {
    setErrors(prev => ({ ...prev, [key]: message }));
  };

  const { mutateAsync: createProgram, isPending: createLoading, error: createError } = useCreateProgram();
  const { mutateAsync: updateProgram, isPending: updateLoading, error: updateError } = useUpdateProgram();
  const { mutateAsync: deleteProgram, isPending: deleteLoading } = useDeleteProgram();
  const { mutateAsync: transferOwnership, isPending: transferOwnerLoading } = useTransferProgramOwnership();
  // TODO: Restore when isPublished flow is fully tested
  // const { mutateAsync: publishProgram, isPending: publishLoading } = usePublishProgram();
  const loading = createLoading || updateLoading;
  const error = createError || updateError;

  // Load existing program data if editing
  useEffect(() => {
    if (isEditMode && programId) {
      setLoadingProgram(true);
      programsApi.get(programId)
        .then(program => {
          setProgramName(program.programName);
          setDescription(program.description);
          setSelectedCategory(PROGRAM_CATEGORIES.find(c => c.value === program.category) || null);
          setWikiUrl(program.wikiUrl || '');
          setSlackChannel(program.slackChannel || '');
          setVisibility(program.visibility === 'org-restricted' ? 'org-restricted' : 'public');
          setOrgLeader(program.orgLeader
            ? { login: program.orgLeader, name: program.orgLeaderName ?? program.orgLeader, firstName: '', lastName: '', email: '', title: '' }
            : null
          );
          setIsProgramOwner(!!(currentUser?.userId && program.ownerId && currentUser.userId === program.ownerId));
          setCohortCount(program.totalCohortCount ?? 0);
          // TODO: Restore when isPublished flow is fully tested
          // setIsPublished(program.isPublished ?? false);
          setBreadcrumbs(buildBreadcrumbs({
            programId: program.programId,
            programName: program.programName,
            currentPage: 'Settings',
          }));
          setLoadingProgram(false);
        })
        .catch(err => {
          console.error('Failed to load program:', err);
          setLoadError('Failed to load program. Please try again.');
          setLoadingProgram(false);
        });
    }
  }, [isEditMode, programId, currentUser?.userId, setBreadcrumbs]);

  // Breadcrumb for create mode
  useEffect(() => {
    if (!isEditMode) {
      setBreadcrumbs(buildBreadcrumbs({ currentPage: 'Create Program' }));
    }
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs, isEditMode]);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!programName.trim()) {
      newErrors.programName = 'Program name is required.';
      isValid = false;
    } else if (!/^[a-zA-Z0-9\s\-_]+$/.test(programName)) {
      newErrors.programName = 'Name can only contain letters, numbers, spaces, dashes, and underscores.';
      isValid = false;
    } else if (programName.length > 200) {
      newErrors.programName = 'Must be 200 characters or less.';
      isValid = false;
    }

    if (!description.trim()) {
      newErrors.description = 'Description is required.';
      isValid = false;
    } else if (description.length > 2000) {
      newErrors.description = 'Must be 2000 characters or less.';
      isValid = false;
    }

    if (!selectedCategory) {
      newErrors.category = 'Category is required.';
      isValid = false;
    }

    if (wikiUrl.trim()) {
      try {
        new URL(wikiUrl);
      } catch {
        newErrors.wikiUrl = 'Please enter a valid URL.';
        isValid = false;
      }
    }

    if (visibility === 'org-restricted' && !orgLeader) {
      newErrors.orgLeader = 'Org leader is required when visibility is restricted.';
      isValid = false;
    }

    setErrors(newErrors);
    
    return isValid;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        const programData: CreateProgramRequest = {
          programName,
          description,
          category: selectedCategory!.value,
          wikiUrl: wikiUrl.trim() || undefined,
          slackChannel: slackChannel.trim() || undefined,
          visibility,
          orgLeader: visibility === 'org-restricted' ? orgLeader?.login : undefined,
          orgLeaderName: visibility === 'org-restricted' ? orgLeader?.name : undefined,
        };

        if (isEditMode && programId) {
          await updateProgram({ programId, data: programData });
          navigate(`/programs/${programId}`);
        } else {
          const createdProgram = await createProgram(programData);

          // Navigate to program details page
          navigate(`/programs/${createdProgram.programId}`);
        }
      } catch (err) {
        console.error('Failed to save program:', err);
      }
    }
  };

  if (loadingProgram) {
    return <LoadingSpinner message="Loading program..." />;
  }

  if (loadError) {
    return <Alert type="error" header="Error loading program">{loadError}</Alert>;
  }

  // In edit mode, only the program owner can access Settings.
  // If currentUser hasn't loaded yet, keep showing the spinner to avoid
  // a flash of "access denied" for the actual owner.
  if (isEditMode && !isProgramOwner) {
    if (!currentUser) return <LoadingSpinner message="Loading..." />;
    return <ProtectedRoute canAccess={false}>{null}</ProtectedRoute>;
  }

  return (
    <>
          <Form
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => navigate(isEditMode ? `/programs/${programId}` : '/programs')}>Cancel</Button>
                <Button variant="primary" onClick={handleSubmit} loading={loading}>
                  {isEditMode ? 'Save changes' : 'Create program'}
                </Button>
              </SpaceBetween>
            }
            header={
              <Header
                description={isEditMode ? "Update program details." : "Create a new training program. You can edit these details later."}
                info={!isEditMode && (
                  <HelpPopover
                    header="Training Programs"
                    content={
                      <>
                        A program is the top-level container for a training initiative. After creating a program, you can create one or more cohorts (program sessions) and build out your team.
                        <br /><br />
                        <strong>You become the program owner.</strong> As owner you can edit and delete the program, add or remove program admins, and transfer ownership. You also have all program admin permissions: creating cohorts, enrolling participants, managing mentors, and reviewing graduation requests.
                      </>
                    }
                  />
                )}
                variant="h1"
              >
                {isEditMode ? 'Program Settings' : 'Create training program'}
              </Header>
            }
            errorText={undefined}
          >
            <SpaceBetween size="l">
              {error && (
                <Alert type="error" header="Unable to save program">
                  {error.message}
                </Alert>
              )}
              {/* TODO: Uncomment when isPublished flow is fully tested
              {isEditMode && !isPublished && (
                <Alert
                  type="info"
                  header="This program is not yet published"
                  action={
                    <Button variant="primary" onClick={() => { setPublishConfirmText(''); setShowPublishModal(true); }}>
                      Publish program
                    </Button>
                  }
                >
                  It is only visible to you and program admins. Publish it when you are ready for users to discover and enroll.
                </Alert>
              )}
              */}
              <Container header={<Header variant="h2">Program details</Header>}>
                <SpaceBetween size="l">
                  <FormField
                    description="Enter a unique name for the program."
                    label="Program name *"
                    errorText={errors.programName}
                  >
                    <Input
                      value={programName}
                      onChange={(event) => {
                        setProgramName(event.detail.value);
                        if (!event.detail.value.trim()) setError('programName', requiredFieldErrors.programName);
                        else if (!/^[a-zA-Z0-9\s\-_]+$/.test(event.detail.value)) setError('programName', 'Name can only contain letters, numbers, spaces, dashes, and underscores.');
                        else if (event.detail.value.length > 200) setError('programName', 'Must be 200 characters or less.');
                        else clearError('programName');
                      }}
                      ariaRequired
                    />
                  </FormField>

                  <FormField
                    description="Provide a clear description of the program objectives and content."
                    label="Description *"
                    stretch
                    errorText={errors.description}
                  >
                    <Textarea
                      rows={3}
                      value={description}
                      onChange={(event) => {
                        setDescription(event.detail.value);
                        if (!event.detail.value.trim()) setError('description', requiredFieldErrors.description);
                        else if (event.detail.value.length > 2000) setError('description', 'Must be 2000 characters or less.');
                        else clearError('description');
                      }}
                      ariaRequired
                    />
                  </FormField>

                  <FormField
                    description="Select the program category."
                    label="Category *"
                    errorText={errors.category}
                  >
                    <Select
                      selectedOption={selectedCategory}
                      onChange={({ detail }) => {
                        setSelectedCategory(detail.selectedOption as { label: string; value: string } | null);
                        if (detail.selectedOption) clearError('category');
                        else setError('category', requiredFieldErrors.category);
                      }}
                      options={PROGRAM_CATEGORIES}
                      placeholder="Choose a category"
                    />
                  </FormField>

                  <FormField
                    description="Link to the program wiki page for additional information and resources."
                    label="Wiki URL — optional"
                    errorText={errors.wikiUrl}
                  >
                    <Input
                      type="url"
                      value={wikiUrl}
                      onChange={(event) => {
                        setWikiUrl(event.detail.value);
                        if (errors.wikiUrl) clearError('wikiUrl');
                      }}
                      onBlur={() => {
                        if (!wikiUrl.trim()) { clearError('wikiUrl'); return; }
                        try { new URL(wikiUrl); clearError('wikiUrl'); }
                        catch { setError('wikiUrl', 'Please enter a valid URL.'); }
                      }}
                    />
                  </FormField>

                  <FormField
                    description="Link to the program's Slack channel for communication."
                    label="Slack channel URL — optional"
                  >
                    <Input
                      type="url"
                      value={slackChannel}
                      onChange={(event) => setSlackChannel(event.detail.value)}
                    />
                  </FormField>

                  <FormField
                    label={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        Enrollment eligibility
                        <HelpPopover
                          header="Enrollment Eligibility"
                          content={
                            <>
                              <strong>Open to all</strong> — any user can enroll in this program (subject to self-enrollment settings on each cohort).
                              <br /><br />
                              <strong>Restricted to org leader or manager reports</strong> — enrollment is limited to users within the selected org leader's reporting chain. Choose this when the program is intended for a specific team or organization.
                            </>
                          }
                        />
                      </span>
                    }
                    description="Restrict enrollment to users within a specific org leader's reporting chain."
                    stretch
                  >
                    <RadioGroup
                      items={[
                        { label: 'Open to all', value: 'public' },
                        { label: 'Restricted to org leader or manager reports', value: 'org-restricted' },
                      ]}
                      value={visibility}
                      onChange={(event) => {
                        setVisibility(event.detail.value as 'public' | 'org-restricted');
                        if (event.detail.value === 'public') {
                          setOrgLeader(null);
                          setErrors(prev => { const next = { ...prev }; delete next.orgLeader; return next; });
                        }
                      }}
                    />
                  </FormField>

                  {visibility === 'org-restricted' && (
                    <FormField
                      description="Search for the org leader or manager whose reports should have access."
                      label="Org leader *"
                      errorText={errors.orgLeader}
                    >
                      <UserSearchSelect
                        onSelect={(user) => {
                          setOrgLeader(user);
                          setErrors(prev => { const next = { ...prev }; delete next.orgLeader; return next; });
                        }}
                        clearOnSelect={false}
                        managersOnly={true}
                        placeholder="Search by name or alias..."
                      />
                      {orgLeader && (
                        <PersonChip
                          login={orgLeader.login}
                          name={orgLeader.name}
                          subtitle={orgLeader.title || undefined}
                          onRemove={() => setOrgLeader(null)}
                          marginTop="8px"
                        />
                      )}
                    </FormField>
                  )}
                </SpaceBetween>
              </Container>
            </SpaceBetween>
          </Form>

          {/* Danger Zone — only shown in edit mode */}
          {isEditMode && (
            <div style={{ marginTop: '24px' }}>
              <Container
                header={
                  <Header
                    variant="h2"
                    description="These actions affect program ownership and existence. They cannot be undone."
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <Icon name="status-warning" variant="error" />
                      Danger Zone
                    </span>
                  </Header>
                }
              >
                <SpaceBetween size="l">
                  {/* Transfer Ownership */}
                  <div>
                    <Button variant="inline-link" onClick={openTransferOwnerModal}>Transfer Ownership</Button>
                    <Box color="text-body-secondary" fontSize="body-s">
                      Transfer ownership to another manager. You can choose to stay on as a program admin.
                    </Box>
                  </div>

                  {/* Delete Program */}
                  <div>
                    <Button variant="inline-link" onClick={openDeleteModal} disabled={cohortCount > 0}>Delete Program</Button>
                    <Box color="text-body-secondary" fontSize="body-s">
                      {cohortCount > 0
                        ? `This program has ${cohortCount} cohort${cohortCount === 1 ? '' : 's'}. Remove all cohorts before deleting the program.`
                        : 'Permanently delete this program and all associated data.'
                      }
                    </Box>
                  </div>
                </SpaceBetween>
              </Container>
            </div>
          )}

          {/* TODO: Uncomment when isPublished flow is fully tested
          Publish Program Modal
          <Modal
            visible={showPublishModal}
            onDismiss={() => setShowPublishModal(false)}
            header="Publish Program"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setShowPublishModal(false)}>Cancel</Button>
                  <Button
                    variant="primary"
                    loading={publishLoading}
                    disabled={publishConfirmText !== 'confirm'}
                    onClick={async () => {
                      try {
                        await publishProgram(programId!);
                        setIsPublished(true);
                        setShowPublishModal(false);
                      } catch (err) {
                        console.error('Failed to publish program:', err);
                      }
                    }}
                  >
                    Publish
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <SpaceBetween size="m">
              <Alert type="warning">
                Once published, this program will be "live" and visible to all users. This cannot be undone.
              </Alert>
              <FormField label={<>Type <strong>confirm</strong> to proceed</>}>
                <Input
                  value={publishConfirmText}
                  onChange={({ detail }) => setPublishConfirmText(detail.value)}
                  placeholder="confirm"
                  autoComplete="off"
                />
              </FormField>
            </SpaceBetween>
          </Modal>
          */}

          {/* Transfer Ownership Modal */}
          <Modal
            visible={showTransferOwnerModal}
            onDismiss={() => setShowTransferOwnerModal(false)}
            header="Transfer Program Ownership"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setShowTransferOwnerModal(false)}>Cancel</Button>
                  <Button
                    variant="primary"
                    disabled={!newOwner || transferConfirmText !== 'confirm'}
                    loading={transferOwnerLoading}
                    onClick={async () => {
                      if (!newOwner) return;
                      try {
                        setTransferOwnerError(null);
                        await transferOwnership({ programId: programId!, newOwnerId: newOwner.login, newOwnerName: newOwner.name, retainAsAdmin });
                        setShowTransferOwnerModal(false);
                        navigate(`/programs/${programId}`);
                      } catch (err: any) {
                        setTransferOwnerError(err.message || 'Failed to transfer ownership');
                      }
                    }}
                  >
                    Transfer Ownership
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <SpaceBetween size="m">
              {transferOwnerError && (
                <Alert type="error" dismissible onDismiss={() => setTransferOwnerError(null)}>{transferOwnerError}</Alert>
              )}
              <Alert type="warning">
                {retainAsAdmin
                  ? 'You will remain a program admin but will no longer be the program owner. Only the new owner will be able to manage admins and transfer ownership.'
                  : 'You will be removed from this program entirely. You will lose all access unless the new owner re-adds you.'}
              </Alert>
              <Checkbox
                checked={retainAsAdmin}
                onChange={({ detail }) => setRetainAsAdmin(detail.checked)}
              >
                Stay on as a program admin after transferring ownership
              </Checkbox>
              <FormField label="New owner" description="Search for a manager to transfer ownership to.">
                <UserSearchSelect
                  key={newOwner?.login ?? 'empty'}
                  onSelect={(user) => setNewOwner(user)}
                  clearOnSelect={false}
                  managersOnly={true}
                  placeholder="Search by name or alias..."
                />
              </FormField>
              {newOwner && (
                <SpaceBetween size="xs">
                  <Box fontWeight="bold">Selected:</Box>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', background: '#f2f3f3', borderRadius: '8px' }}>
                    <img src={badgePhotoUrl(newOwner.login)} alt={newOwner.name} style={{ width: 40, height: 40, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Box fontWeight="bold">{newOwner.name}</Box>
                      <Box color="text-body-secondary" fontSize="body-s"><Link href={phoneToolUrl(newOwner.login)} external fontSize="body-s">@{newOwner.login}</Link> — {newOwner.title}</Box>
                    </div>
                    <Button variant="icon" iconName="close" ariaLabel="Clear selection" onClick={() => setNewOwner(null)} />
                  </div>
                </SpaceBetween>
              )}
              <FormField label={<>Type <strong>confirm</strong> to proceed</>}>
                <Input
                  value={transferConfirmText}
                  onChange={({ detail }) => setTransferConfirmText(detail.value)}
                  placeholder="confirm"
                  autoComplete="off"
                />
              </FormField>
            </SpaceBetween>
          </Modal>

          {/* Delete Program Modal */}
          <Modal
            visible={showDeleteModal}
            onDismiss={() => setShowDeleteModal(false)}
            header="Delete Program"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                  <Button
                    variant="primary"
                    loading={deleteLoading}
                    disabled={deleteConfirmText !== 'confirm'}
                    onClick={async () => {
                      try {
                        setDeleteError(null);
                        await deleteProgram(programId!);
                        navigate('/programs');
                      } catch (err: any) {
                        setDeleteError(err.message || 'Failed to delete program');
                      }
                    }}
                  >
                    Delete
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <SpaceBetween size="m">
              {deleteError && (
                <Alert type="error" dismissible onDismiss={() => setDeleteError(null)}>{deleteError}</Alert>
              )}
              <Alert type="warning">
                Are you sure you want to permanently delete this program?
                This will remove all associated admin roles and cannot be undone.
              </Alert>
              <FormField label={<>Type <strong>confirm</strong> to proceed</>}>
                <Input
                  value={deleteConfirmText}
                  onChange={({ detail }) => setDeleteConfirmText(detail.value)}
                  placeholder="confirm"
                  autoComplete="off"
                />
              </FormField>
            </SpaceBetween>
          </Modal>
    </>
  );
}

export default CreateProgram;